import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal, LineReveal, FadeIn } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { SEO_META, INDUSTRIES, WHY_POINTS, PROCESS_STEPS, TRUST_POINTS, FAQS, MARQUEE_ITEMS, IMAGES, waUrl, WA_GENERAL } from "../config/site";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const BENTO_SPANS = ["lg:col-span-3", "lg:col-span-3", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-2", "lg:col-span-3", "lg:col-span-3"];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yA = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const yB = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative overflow-hidden border-b border-obsidian/10" data-testid="home-hero">
      <div className="container-site grid items-center gap-12 py-16 sm:py-20 lg:grid-cols-12 lg:gap-8 lg:py-24">
        <div className="lg:col-span-7">
          <FadeIn>
            <p className="eyebrow">B2B Uniform Manufacturing · India</p>
          </FadeIn>
          <h1 className="mt-5 font-display text-4xl font-black leading-[1.06] tracking-tight text-obsidian sm:text-5xl lg:text-6xl">
            <LineReveal lines={["Uniforms built for", "the way your", "business works."]} />
          </h1>
          <FadeIn delay={0.55}>
            <p className="mt-4 font-display text-lg font-bold text-obsidian/80 sm:text-xl">
              Custom uniform manufacturing for businesses, institutions and professional teams worldwide.
            </p>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-obsidian/60">
              From design and fabric selection to bulk production and export-ready supply, Cleef Empire helps
              businesses create professional uniforms tailored to their people, brand and working environment.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="hero-cta-bulk-quote" className="btn-primary">
                Get Bulk Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/uniforms" data-testid="hero-cta-explore" className="btn-outline">
                Explore Uniforms
              </Link>
            </div>
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.18em] text-obsidian/45">
              B2B Manufacturing · Custom Development · Bulk Orders · Export Supply
            </p>
          </FadeIn>
        </div>

        <div className="lg:col-span-5">
          <div className="grid grid-cols-2 gap-4">
            <motion.div style={{ y: yA }} className="col-span-2">
              <FadeIn delay={0.35}>
                <div className="frame-offset overflow-hidden">
                  <img src={IMAGES.healthcare} alt="Medical professionals in clean healthcare uniforms" className="aspect-[16/9] w-full object-cover" loading="eager" />
                </div>
              </FadeIn>
            </motion.div>
            <motion.div style={{ y: yB }}>
              <FadeIn delay={0.5}>
                <div className="overflow-hidden">
                  <img src={IMAGES.airline} alt="Airline cabin crew in tailored uniforms" className="aspect-[3/4] w-full object-cover" loading="eager" />
                </div>
              </FadeIn>
            </motion.div>
            <motion.div style={{ y: yA }}>
              <FadeIn delay={0.62}>
                <div className="overflow-hidden">
                  <img src={IMAGES.chef} alt="Hotel chef in a professional hospitality uniform" className="aspect-[3/4] w-full object-cover" loading="eager" />
                </div>
              </FadeIn>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Marquee() {
  const row = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="overflow-hidden border-b border-obsidian/10 bg-bone py-4" aria-hidden="true">
      <div className="marquee-track">
        {[0, 1].map((half) => (
          <div key={half} className="flex shrink-0 items-center">
            {row.map((item, i) => (
              <span key={`${half}-${i}`} className="flex items-center font-mono text-xs font-semibold uppercase tracking-[0.28em] text-obsidian/55">
                <span className="px-6">{item}</span>
                <span className="text-cleef">·</span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function IndustriesSection() {
  return (
    <section className="container-site py-20 lg:py-28" data-testid="industries-section">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="eyebrow">Industries</p>
            <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
              Uniforms for every role
            </h2>
            <p className="mt-3 max-w-lg text-base text-obsidian/60">
              Purpose-built workwear for industries where appearance, comfort and performance matter.
            </p>
          </div>
          <Link to="/industries" className="group inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-obsidian transition-colors hover:text-cleef" data-testid="industries-view-all-link">
            View all industries <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </Reveal>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
        {INDUSTRIES.map((ind, i) => (
          <Reveal key={ind.slug} delay={(i % 3) * 0.08} className={`${BENTO_SPANS[i]}`}>
            <Link
              to={`/uniforms`}
              data-testid={`industry-card-${ind.slug}`}
              className="group relative block overflow-hidden bg-obsidian"
            >
              <img
                src={ind.img}
                alt={ind.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover opacity-90 transition-all duration-700 ease-out group-hover:scale-[1.06] group-hover:opacity-100"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-obsidian/90 via-obsidian/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-5">
                <h3 className="font-display text-lg font-bold leading-tight text-white">{ind.name}</h3>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-white/65">{ind.line}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80 transition-colors group-hover:text-cleef">
                  Explore <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

function WhySection() {
  return (
    <section className="border-y border-obsidian/10 bg-white" data-testid="why-section">
      <div className="container-site py-20 lg:py-28">
        <Reveal>
          <p className="eyebrow">Why Cleef Empire</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
            A manufacturing partner built around B2B buyers
          </h2>
        </Reveal>
        <div className="mt-14 grid gap-px overflow-hidden border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2 lg:grid-cols-3">
          {WHY_POINTS.map((w, i) => (
            <Reveal key={w.title} delay={(i % 3) * 0.08} className="bg-white">
              <div className="group h-full p-8 transition-colors duration-300 hover:bg-ivory">
                <span className="font-mono text-xs font-semibold tracking-[0.2em] text-cleef">0{i + 1}</span>
                <h3 className="mt-4 font-display text-xl font-bold tracking-tight">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{w.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function ProcessSection() {
  return (
    <section className="bg-obsidian text-white" data-testid="process-section">
      <div className="container-site py-20 lg:py-28">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow !text-white/50">From idea to delivery</p>
              <h2 className="mt-3 max-w-xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
                From first specification to final shipment
              </h2>
            </div>
            <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid="process-cta-btn" className="btn-primary">
              Start Your Requirement <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </Reveal>
        <div className="mt-14 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
          {PROCESS_STEPS.map((s, i) => (
            <Reveal key={s.n} delay={(i % 3) * 0.08} className="bg-obsidian">
              <div className="h-full border-t-2 border-transparent p-8 transition-colors duration-300 hover:border-cleef">
                <span className="font-mono text-3xl font-semibold text-cleef">{s.n}</span>
                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-[0.04em]">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function TrustSection() {
  return (
    <section className="container-site py-20 lg:py-28" data-testid="trust-section">
      <div className="grid items-center gap-12 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="eyebrow">Buyer confidence</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
              What international buyers need
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-obsidian/60">
              Sourcing uniforms across borders should feel structured and predictable. This is how we work
              with procurement teams, importers and distributors.
            </p>
            <div className="frame-offset mt-8 overflow-hidden">
              <img src={IMAGES.embroidery} alt="Detailed embroidery stitching on a custom uniform garment" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-7">
          <div className="grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2">
            {TRUST_POINTS.map((t, i) => (
              <Reveal key={t.title} delay={(i % 2) * 0.08} className="bg-ivory">
                <div className="h-full p-7">
                  <h3 className="font-display text-base font-bold tracking-tight">{t.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{t.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  return (
    <section className="border-t border-obsidian/10 bg-white" data-testid="faq-section">
      <div className="container-site grid gap-12 py-20 lg:grid-cols-12 lg:py-28">
        <div className="lg:col-span-4">
          <Reveal>
            <p className="eyebrow">FAQ</p>
            <h2 className="mt-3 font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
              Questions buyers ask us
            </h2>
            <p className="mt-4 text-base leading-relaxed text-obsidian/60">
              Straight answers for procurement teams evaluating a new manufacturing partner.
            </p>
            <a
              href={waUrl(WA_GENERAL)}
              target="_blank"
              rel="noopener noreferrer"
              data-event="whatsapp_click"
              data-testid="faq-whatsapp-btn"
              className="mt-7 inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-obsidian transition-colors hover:text-[#128C7E]"
            >
              <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> Ask on WhatsApp
            </a>
          </Reveal>
        </div>
        <div className="lg:col-span-8">
          <Reveal>
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-obsidian/10">
                  <AccordionTrigger className="text-left font-display text-base font-bold tracking-tight hover:text-cleef sm:text-lg" data-testid={`faq-question-${i}`}>
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-obsidian/60 sm:text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const meta = SEO_META.home;
  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <Hero />
      <Marquee />
      <IndustriesSection />
      <WhySection />
      <ProcessSection />
      <TrustSection />
      <FAQSection />
      <CTABlock />
    </>
  );
}
